/* DCG V3.1.2 · 21 OCT 2026 · item 7's end gate: an answer with products that StorePass draws late keeps the bar and the copy up until its cards come (0.9 s after the answer only for an answer with none, or one that changes nothing; 4 s at most) */
/* DCG V3.1.2 · 20 OCT 2026 · item 6's gate: the held copy kept exactly over the grid every frame until it is gone (it sat 20px off and jumped back as it faded: a layout shift of 0.005, 0.015 on a phone) */
/* DCG V3.1.2 · 20 OCT 2026 · item 7's gate: the answer is drawn when StorePass's cards differ from those of the search's start (or its placeholders came and went), not when cards are there after the answer: /search keeps its old cards while it searches, and on a slow line the bar and the copy went before the new cards were drawn */
/* DCG V3.1.2 · 20 OCT 2026 · item 7 (Cole, 20 OCT; docs/PROMPT-V3-1-2-adjustments.md): the loading bar on every StorePass listing, its easing set by the median of the device's last five searches, its line "Searching … 1.2 s" in the count line's place */
/* DCG V3.1.2 · 20 OCT 2026 · item 6 (Cole, 20 OCT; docs/PROMPT-V3-1-2-adjustments.md): while a StorePass listing searches, the grid stays on screen under a dim overlay instead of blanking */
/* =========================================================
   Desert City Games — a StorePass listing while it searches
   File: assets/dcg-storepass-loading.js   (layout/theme.liquid, with the other StorePass scripts; its look:
   assets/dcg-storepass.css, "V3.1.2 THE LISTING WHILE IT SEARCHES")

   Measured on the draft as first pushed (tests/skin/loadbar-probe.js,
   tests/skin/filter-requests-probe.js; docs/img/v3-1-2/before/): at each
   filter change, sort or page, storepass.js takes its cards away the moment
   it searches and draws placeholders until the answer comes: the grid had no
   cards for 0.5 to 0.9 s on a fast connection and up to 7.8 s on a slow one,
   and the page under it moved (a layout shift of up to 0.56 on Advanced
   Search, the footer coming up while the grid was empty).

   THE HOLD. When a search of the listing itself leaves (the guard says so:
   'dcg:storepass-search-start'; a page of cards at the listing's page size,
   not a count, not a value list) and the listing shows cards, a copy of its
   grid is put over the grid's own place (aria-hidden, inert, not pressable),
   dimmed, and the grid's place keeps its height, so nothing under it moves.
   When storepass.js has drawn the answer (cards, or a page with none), when
   the search fails, when the collection safety net takes the listing, or
   after 12 seconds, the copy goes (a short fade; none under
   prefers-reduced-motion) and the place its height. A search that follows
   while the copy is up keeps the same copy. Nothing of StorePass's own is
   changed but the grid's min-height while the copy is up; the copy sits
   after StorePass's grid, so a script that reads the listing's first grid
   reads StorePass's (assets/dcg-storepass-paging.js).
   The page's first load has no cards to hold.

   THE BAR (V3.1.2, item 7). With every search of the listing, the page's
   first load's too, a loading bar along the bottom of StorePass's results
   bar and a line in the count line's place ("Searching 5,707 Pokemon
   Singles… 1.2 s"); see "The loading bar" below.

   Nothing here may break the page: every step is wrapped.
   ========================================================= */
(function () {
  'use strict';

  window.DCG = window.DCG || {};
  if (window.DCG.storepassLoading) return;

  const listing = () => document.getElementById('product-listing-container');
  const HOLD_PATIENCE = 12000;
  const FADE_MS = 150;
  const reduceMotion = () => { try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (_) { return false; } };

  // a search of the listing itself: a page of cards at the listing's page size (the guard's)
  function listingSearch(detail) {
    if (!detail || detail.isCount || !detail.query) return false;
    const q = detail.query;
    if (q.return_type || !q.product_line) return false;
    let size = null;
    try { const g = window.DCG.storepassGuard; size = g && typeof g.pageSize === 'function' ? g.pageSize() : null; } catch (_) { size = null; }
    return size === null || parseInt(q.limit, 10) === size;
  }

  // ── The hold ──
  let hold = null;           // { node, section, host, timer, observer, minHeight }
  function realSection(root) { return root ? root.querySelector('.store-pass-products-section') : null; }
  function hasCards(section) { return !!section && !!section.querySelector(':scope > .store-pass-product') && !section.querySelector('.store-pass-product-placeholder'); }
  // StorePass's cards in the grid, in order. Some listings (/search) keep the old cards while they search, with no
  // placeholders: the answer is drawn when the cards are no longer those of the search's start (other cards, or the same
  // in another order); an answer that changes nothing is taken as drawn a while after it came.
  function cardNodes(section) { return section ? Array.prototype.slice.call(section.querySelectorAll(':scope > .store-pass-product')) : []; }
  function redrawn(section, before) {
    const now = cardNodes(section);
    if (!now.length) return false;
    if (!before || now.length !== before.length) return true;
    for (let i = 0; i < now.length; i++) if (now[i] !== before[i]) return true;
    return false;
  }
  const SAME_ANSWER_MS = 900;
  // an answer with products that StorePass has not drawn this long after it: the copy and the bar go all the same
  const ANSWER_PATIENCE = 4000;

  function placeHold() {
    const root = listing();
    if (!root || root.hasAttribute('data-dcg-fallback')) return;
    if (hold) { armTimer(); return; }                       // the next change's search: the same copy stays
    const section = realSection(root);
    if (!hasCards(section) || !section.parentElement) return;
    const host = section.parentElement;
    const box = section.getBoundingClientRect(), hbox = host.getBoundingClientRect();
    if (!(box.height > 0)) return;
    const copy = section.cloneNode(true);
    copy.removeAttribute('id');
    copy.querySelectorAll('[id]').forEach(n => n.removeAttribute('id'));
    copy.querySelectorAll('input, select, textarea, button').forEach(n => { n.removeAttribute('name'); n.setAttribute('tabindex', '-1'); });
    const node = document.createElement('div');
    node.className = 'dcg-sp-hold';
    node.setAttribute('data-dcg-hold', '');
    node.setAttribute('aria-hidden', 'true');
    try { node.inert = true; } catch (_) {}
    node.style.left = (box.left - hbox.left + host.scrollLeft) + 'px';
    node.style.top = (box.top - hbox.top + host.scrollTop) + 'px';
    node.style.width = box.width + 'px';
    node.style.height = box.height + 'px';
    node.appendChild(copy);
    host.setAttribute('data-dcg-hold-host', '');
    host.insertBefore(node, section.nextSibling);
    const minHeight = section.style.minHeight;
    section.style.minHeight = Math.ceil(box.height) + 'px';
    root.setAttribute('data-dcg-loading', '');
    hold = { node, section, host, timer: null, observer: null, minHeight, sawLoading: false, answered: false, atStart: cardNodes(section), raf: 0 };
    followGrid(node, section);
    // the answer drawn: StorePass's own grid holds cards again, or a page with none
    try {
      hold.observer = new MutationObserver(() => maybeRelease(false));
      hold.observer.observe(section, { childList: true, subtree: true });
    } catch (_) {}
    armTimer();
  }
  // The copy kept exactly over the grid, every frame until it is gone (its fade too): measured on the draft (20 OCT
  // 23:40), the copy sat 20px below the grid from the moment it was placed (a rule on the grid's parent's children moved
  // it; assets/dcg-storepass.css now takes its margin away), and the grid moved under it when StorePass took its
  // In Stock Only pill away; the copy's own jump back as it faded was a layout shift of 0.005 (0.015 on a phone).
  function alignTo(node, section) {
    try {
      const a = section.getBoundingClientRect(), b = node.getBoundingClientRect();
      if (!(b.width > 0 && b.height > 0 && a.width > 0)) return;       // not laid out (yet): nothing to measure
      const dy = a.top - b.top, dx = a.left - b.left;
      if (Math.abs(dy) >= 0.5) node.style.top = (parseFloat(node.style.top) + dy).toFixed(1) + 'px';
      if (Math.abs(dx) >= 0.5) node.style.left = (parseFloat(node.style.left) + dx).toFixed(1) + 'px';
    } catch (_) {}
  }
  function followGrid(node, section) {
    const raf = window.requestAnimationFrame || (f => setTimeout(f, 16));
    alignTo(node, section);
    const step = () => { if (!node.isConnected || !section.isConnected) return; alignTo(node, section); raf(step); };
    raf(step);
  }
  // The copy goes once StorePass has drawn the answer: its cards after its placeholders, or cards other than those of
  // the search's start; or, the answer come with no products, a grid with none (its "No results found"); or (`last`, a
  // while after the answer) a grid the answer did not change. A grid emptied while an answer with products is still to
  // be drawn keeps the copy (until it is drawn, or ANSWER_PATIENCE after the answer).
  function maybeRelease(last) {
    if (!hold) return;
    const s = hold.section;
    if (!s || !s.isConnected) { releaseHold(); return; }
    if (s.querySelector('.store-pass-product-placeholder')) { hold.sawLoading = true; return; }
    const cards = !!s.querySelector(':scope > .store-pass-product');
    if (cards && (hold.sawLoading || redrawn(s, hold.atStart))) { releaseHold(); return; }
    if (!hold.answered) return;
    if (!cards && hold.none) { releaseHold(); return; }
    if (cards && last === true) releaseHold();
  }
  const frames = (n, done) => { if (n <= 0) { done(); return; } (window.requestAnimationFrame || (f => setTimeout(f, 16)))(() => frames(n - 1, done)); };
  function onAnswer(e) {
    const d = e && e.detail;
    if (!listingSearch(d)) return;
    barAnswered(d);
    if (!hold) return;
    hold.answered = true;
    hold.none = d.products === 0;
    // storepass.js draws the answer just after the guard hears it
    const h = hold;
    frames(2, () => maybeRelease(false));
    setTimeout(() => maybeRelease(false), 300);
    setTimeout(() => maybeRelease(true), SAME_ANSWER_MS);
    setTimeout(() => { if (hold === h) releaseHold(); }, ANSWER_PATIENCE);
  }
  function armTimer() {
    if (!hold) return;
    clearTimeout(hold.timer);
    hold.timer = setTimeout(releaseHold, HOLD_PATIENCE);
  }
  function releaseHold() {
    if (!hold) return;
    const h = hold;
    hold = null;
    clearTimeout(h.timer);
    try { if (h.observer) h.observer.disconnect(); } catch (_) {}
    try { h.section.style.minHeight = h.minHeight || ''; } catch (_) {}
    const root = listing();
    if (root) root.removeAttribute('data-dcg-loading');
    const gone = () => { try { h.node.remove(); } catch (_) {} try { if (!h.host.querySelector(':scope > [data-dcg-hold]')) h.host.removeAttribute('data-dcg-hold-host'); } catch (_) {} };
    if (reduceMotion()) { gone(); return; }
    h.node.setAttribute('data-dcg-hold-out', '');
    setTimeout(gone, FADE_MS + 20);
  }

  // ── The loading bar (V3.1.2, item 7) ──
  // A 3px orange bar along the bottom of StorePass's results bar (the toolbar: the count, Sort, the view switch), laid
  // over its edge so it takes no space (nothing moves when it comes or goes). It starts when the listing's search leaves,
  // rises on an easing curve that reaches 0.7 at the median of this device's last five searches (sessionStorage
  // dcg_sp_times; 900 ms before there is one) and holds just under 0.9 however long StorePass takes, snaps to the end
  // when the cards are drawn and fades. In the count line's place while it runs: "Searching 5,707 Pokemon Singles…
  // 1.2 s" (the collection's own count and title, as the page prints them; "Searching for "charizard"…" on /search; the
  // game's singles on Advanced Search), the seconds live; once the cards are drawn the count line is back ("Showing
  // 1-60 of 5,707", the paging script's). The page's first load is the same search: the bar runs from it, under the
  // page's own loader while that is up (#siteloader is left as it is). Under prefers-reduced-motion the bar is drawn
  // full and still while the search is out, and goes at once.
  const TIMES_KEY = 'dcg_sp_times';
  const FIRST_GUESS_MS = 900;
  const BAR_PATIENCE = 20000;
  function pastTimes() {
    try { const a = JSON.parse(window.sessionStorage.getItem(TIMES_KEY) || '[]'); return Array.isArray(a) ? a.filter(n => typeof n === 'number' && n > 0 && n < 60000).slice(-5) : []; }
    catch (_) { return []; }
  }
  function keepTime(ms) {
    try { const a = pastTimes(); a.push(Math.round(ms)); window.sessionStorage.setItem(TIMES_KEY, JSON.stringify(a.slice(-5))); } catch (_) {}
  }
  function median(list) {
    if (!list.length) return FIRST_GUESS_MS;
    const s = list.slice().sort((x, y) => x - y), m = Math.floor(s.length / 2);
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
  }
  // the fill at t ms into a search: 0.7 at the median, never above 0.9 until the cards are drawn
  const fillAt = (t, med) => 0.9 * (1 - Math.exp(-t * Math.log(4.5) / Math.max(150, med)));
  const fmtCount = n => Number(n).toLocaleString('en-CA');

  // what is being searched, in the page's own words
  function searchingWords() {
    const root = listing();
    if (!root) return 'Searching…';
    try {
      const adv = window.DCG.storepassAdvSearch;
      if (adv && typeof adv.game === 'function' && adv.game()) {
        const g = (adv.games() || []).find(x => x.slug === adv.game());
        const name = adv.name && adv.name();
        if (name) return 'Searching ' + (g ? g.name + ' ' : '') + 'singles for “' + name + '”…';
        return 'Searching ' + (g ? g.name + ' ' : '') + 'singles…';
      }
    } catch (_) {}
    const count = parseInt(root.getAttribute('data-dcg-shopify-count'), 10);
    const title = root.getAttribute('data-dcg-collection-title');
    if (title) return 'Searching ' + (count > 0 ? fmtCount(count) + ' ' : '') + title + '…';
    try { const q = new URLSearchParams(window.location.search).get('q'); if (q) return 'Searching for “' + q + '”…'; } catch (_) {}
    return 'Searching…';
  }

  let bar = null;            // { node, fill, text, host, started, med, raf, timer, ticker, done }
  function barHost(root) { return root.querySelector('.store-pass-results-header') || null; }
  function ensureBar() {
    const root = listing();
    if (!root) return null;
    const host = barHost(root);
    if (!host) return null;
    let node = host.querySelector(':scope > [data-dcg-loadbar]');
    if (!node) {
      node = document.createElement('div');
      node.className = 'dcg-loadbar';
      node.setAttribute('data-dcg-loadbar', '');
      node.setAttribute('aria-hidden', 'true');
      node.innerHTML = '<div class="dcg-loadbar__track"><div class="dcg-loadbar__fill"></div></div>';
      host.setAttribute('data-dcg-loadbar-host', '');
      host.appendChild(node);
    }
    let text = host.querySelector(':scope > .dcg-loadbar__text');
    if (!text) {
      text = document.createElement('div');
      text.className = 'dcg-loadbar__text';
      text.setAttribute('role', 'status');
      text.setAttribute('aria-live', 'polite');
      host.appendChild(text);
    }
    return { node, fill: node.querySelector('.dcg-loadbar__fill'), text, host };
  }
  // the line is put where the count line is (left of the bar, on the count's own line); with none yet, at the bar's start
  function placeText(b) {
    try {
      const line = b.host.querySelector('.dcg-count:not(.dcg-count--foot)') || b.host.querySelector('.store-pass-results-count');
      const hb = b.host.getBoundingClientRect();
      if (line && line.getClientRects().length) {
        const r = line.getBoundingClientRect();
        b.text.style.left = Math.round(r.left - hb.left) + 'px';
        b.text.style.top = Math.round(r.top - hb.top) + 'px';
        b.text.style.minHeight = Math.round(r.height) + 'px';
      } else { b.text.style.left = '0px'; b.text.style.top = '0px'; b.text.style.minHeight = ''; }
    } catch (_) {}
  }
  let barTries = 0;
  function startBar() {
    const root = listing();
    if (!root || root.hasAttribute('data-dcg-fallback')) return;
    if (bar && !bar.done) return;                         // the next change's search: the same run goes on
    const b = ensureBar();
    if (!b) { if (++barTries <= 40) setTimeout(() => { if (!bar) startBar(); }, 100); return; }      // the results bar is not drawn yet (the first load): up to 4 s
    barTries = 0;
    const med = median(pastTimes());
    bar = Object.assign(b, { started: performance.now(), med, raf: 0, timer: 0, ticker: 0, done: false, answered: false, sawLoading: false, atStart: [] });
    // (the first load: StorePass's placeholders may be up already when the bar starts)
    try { const sec = root.querySelector('.store-pass-products-section'); if (sec && sec.querySelector('.store-pass-product-placeholder')) bar.sawLoading = true; bar.atStart = cardNodes(sec); } catch (_) {}
    const words = searchingWords();
    placeText(bar);
    root.setAttribute('data-dcg-searching', '');
    bar.node.removeAttribute('data-dcg-loadbar-out');
    bar.node.setAttribute('data-dcg-loadbar-on', '');
    const still = reduceMotion();
    bar.fill.style.transition = 'none';
    bar.fill.style.transform = still ? 'scaleX(1)' : 'scaleX(0)';
    const say = () => { if (!bar || bar.done) return; bar.text.textContent = words + ' ' + ((performance.now() - bar.started) / 1000).toFixed(1) + ' s'; };
    say();
    bar.ticker = setInterval(say, 100);
    if (!still) {
      const step = () => {
        if (!bar || bar.done) return;
        bar.fill.style.transform = 'scaleX(' + fillAt(performance.now() - bar.started, bar.med).toFixed(4) + ')';
        bar.raf = window.requestAnimationFrame(step);
      };
      bar.raf = window.requestAnimationFrame(step);
    }
    bar.timer = setTimeout(() => endBar(false), BAR_PATIENCE);
  }
  function endBar(drawn) {
    if (!bar || bar.done) return;
    const b = bar;
    b.done = true;
    clearInterval(b.ticker); clearTimeout(b.timer);
    try { window.cancelAnimationFrame(b.raf); } catch (_) {}
    if (drawn) keepTime(performance.now() - b.started);
    const root = listing();
    if (root) root.removeAttribute('data-dcg-searching');
    b.text.textContent = '';
    const finish = () => { b.node.removeAttribute('data-dcg-loadbar-on'); b.node.removeAttribute('data-dcg-loadbar-out'); b.fill.style.transition = 'none'; b.fill.style.transform = 'scaleX(0)'; if (bar === b) bar = null; };
    if (reduceMotion()) { finish(); return; }
    b.fill.style.transition = 'transform 120ms ease-out';
    b.fill.style.transform = 'scaleX(1)';
    setTimeout(() => { b.node.setAttribute('data-dcg-loadbar-out', ''); setTimeout(finish, 260); }, 140);
  }
  // the cards drawn (or a page with none): StorePass's own grid, its cards after its placeholders, or cards other than
  // those of the search's start
  let barWatch = null;
  function watchDraw() {
    const root = listing();
    const section = root && root.querySelector('.store-pass-products-section');
    if (!section || barWatch === section) return;
    barWatch = section;
    try {
      new MutationObserver(() => {
        if (!bar || bar.done) return;
        if (section.querySelector('.store-pass-product-placeholder')) { bar.sawLoading = true; return; }
        if (section.querySelector(':scope > .store-pass-product') && (bar.sawLoading || redrawn(section, bar.atStart))) endBar(true);
      }).observe(section, { childList: true, subtree: true });
    } catch (_) {}
  }
  function barAnswered(detail) {
    if (!bar || bar.done) return;
    bar.answered = true;
    const none = !!detail && detail.products === 0;      // an answer with no products: StorePass's "No results found"
    // its cards drawn; or, a while after the answer, none for an answer with none, or the same cards as before (an
    // answer that changes nothing). An answer with products not drawn yet keeps the bar up until they are (the first
    // load on a phone: StorePass drew its cards more than 0.9 s after the answer, 21 OCT 00:24), or ANSWER_PATIENCE.
    const b = bar;
    const check = last => {
      if (!bar || bar.done) return;
      const root = listing(), section = root && root.querySelector('.store-pass-products-section');
      if (section && section.querySelector('.store-pass-product-placeholder')) return;
      const cards = !!section && !!section.querySelector(':scope > .store-pass-product');
      if (cards && (bar.sawLoading || redrawn(section, bar.atStart))) { endBar(true); return; }
      if (last && (none || cards)) endBar(true);
    };
    frames(2, () => check(false));
    setTimeout(() => check(false), 300);
    setTimeout(() => check(true), SAME_ANSWER_MS);
    setTimeout(() => { if (bar === b) endBar(false); }, ANSWER_PATIENCE);
  }

  function onStart(e) {
    const d = e && e.detail;
    if (!listingSearch(d)) return;
    placeHold();
    watchDraw();
    startBar();
  }
  function onFailed(e) {
    const d = e && e.detail;
    if (!hold || !d || d.isCount || !listingSearch(d)) return;
    // the search failed: what StorePass (or the safety net) then draws is what the page shows
    hold.answered = true;
    setTimeout(maybeRelease, 50);
    setTimeout(releaseHold, 1500);
  }
  function onFailedBar(e) {
    const d = e && e.detail;
    if (!d || d.isCount || !listingSearch(d)) return;
    setTimeout(() => endBar(false), 600);
  }

  function start() {
    if (!listing()) return;
    document.addEventListener('dcg:storepass-search-start', e => { try { onStart(e); } catch (_) {} });
    document.addEventListener('dcg:storepass-search', e => { try { onAnswer(e); } catch (_) {} });
    document.addEventListener('dcg:storepass-search-failed', e => { try { onFailed(e); } catch (_) {} try { onFailedBar(e); } catch (_) {} });
    // the collection safety net takes the listing: the copy goes at once
    document.addEventListener('dcg:collection-fallback', () => { try { releaseHold(); } catch (_) {} try { endBar(false); } catch (_) {} });
  }

  window.DCG.storepassLoading = {
    holding: () => !!hold,
    release: releaseHold,
    // V3.1.2 (item 7): for checking
    bar: () => (bar ? { started: bar.started, median: bar.med, done: bar.done } : null),
    times: pastTimes
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { try { start(); } catch (_) {} });
  else { try { start(); } catch (_) {} }
})();
